import { useNavigate } from 'react-router-dom'
import { User, Settings, LogOut } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { getInitials } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

// Avatar + two stacked lines (name over role) at the end of the header.
export function UserMenu() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  const displayName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'User'
  const initials = getInitials(displayName)
  const roleLabel = user?.is_company_admin
    ? 'Company Admin'
    : typeof user?.role === 'string' && user.role
      ? user.role
      : 'Member'

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full p-1 outline-none xl:pr-2 transition-colors duration-300 hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-black/40"
        >
          <Avatar className="h-8 w-8">
            {user?.profile_picture && (
              <AvatarImage src={user.profile_picture} alt={displayName} />
            )}
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden min-w-0 flex-col items-start text-left xl:flex">
            <span className="max-w-[140px] truncate text-[12px] font-semibold leading-tight text-clay-900 dark:text-clay-100">
              {displayName}
            </span>
            <span className="max-w-[140px] truncate text-[10px] font-semibold leading-tight text-clay-500 dark:text-clay-400">
              {roleLabel}
            </span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 max-w-[calc(100vw-2rem)]" sideOffset={8}>
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar className="h-10 w-10">
            {user?.profile_picture && (
              <AvatarImage src={user.profile_picture} alt={displayName} />
            )}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="heading-text truncate text-sm font-bold">{displayName}</p>
            {user?.email && (
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
          <User />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <Settings />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-error focus:bg-error-subtle focus:text-error"
        >
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
